import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { DSSetup, PopupData, RecordData, Service } from 'webapp-types';
import { ModelName, TeamSectionItemType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import TeamsPopupAddItemComponent from 'webapp/components/teams/popup-add-item';
import TeamsSectionAddItemPopupComponent from 'webapp/components/teams/view/section/popup-add-item';

import type { TeamSectionItem } from 'webapp/models-custom';
import type InfolayerItemGeneral from 'webapp/models-custom/infolayer-item/general';
import MetricSetup from 'webapp/models-custom/metric-setup';
import TeamSection from 'webapp/models-custom/team-section';
import type TeamSectionRecord from 'webapp/models/team-section';

import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';

export interface TeamSectionItemSnapshot {
	objectType: TeamSectionItemType;
	objectId?: number;
	objectData?: RecordData.TeamSectionItem.ObjectData;
}

export default class ModelManagerTeamSectionsService extends ModelManagerForStoreService<
	TeamSectionRecord,
	TeamSection
> {
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/team-section-items')
	declare mmTeamSectionItemsService: Service.ModelManager.TeamSectionItems;

	private _sectionModels: TeamSection[] = [];

	@cached
	get sectionModels(): TeamSection[] {
		this.updateModelCache(this._sectionModels, this.savedRecords);
		return this._sectionModels;
	}

	createNew(teamId: string) {
		const infolayer = this.infolayerService.loading('Creating section...');

		return this.requestService
			.api('POST', `/teams/${teamId}/sections`)
			.then(() => {
				infolayer.ok('Section created!');
			})
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}

	delete(section: TeamSection) {
		if (this.accountService.handleDemo()) {
			return;
		}

		const affirmativeFunc = () => {
			const infolayer = this.infolayerService.loading('Deleting section...');

			this.analyticsService.event(TrackingEvent.TEAM_PAGE_EDITED, {
				action: ['section deleted'],
			});

			section
				.delete()
				.then(
					() => {
						infolayer.ok('Section deleted!');
					},
					(error) => {
						infolayer.close();
						throw error;
					},
				)
				.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
		};

		this.popupService.openConfirmPopup({
			onConfirm: affirmativeFunc,
			title: `Delete "${section.name}" section?`,
			text: 'This will remove the section, and all items it contains. <strong> This cannot be undone. </strong>',
			confirmAction: 'Delete section',
			cancelAction: 'Cancel',
		});
	}

	batchAddItems(
		section: TeamSection,
		items: TeamSectionItemSnapshot[],
		{
			isInfolayerShown = true,
			isTrackingDisabled = false,
		}: {
			isInfolayerShown?: boolean;
			isTrackingDisabled?: boolean;
		} = {},
	) {
		const teamId = section.teamId;
		let infolayer: InfolayerItemGeneral | null = null;
		if (isInfolayerShown) {
			infolayer = this.infolayerService.loading(
				`Adding item${items.length > 1 ? 's' : ''} to the section...`,
			);
		}

		// filter out duplicate items
		const currentItems = section.items;

		const newItems = items.filter(
			(item) => !currentItems.some((currentItem) => this.compareFunc(currentItem, item)),
		);
		const duplicateCount = items.length - newItems.length,
			newItemsCount = newItems.length;

		if (duplicateCount > 0 && newItemsCount === 0) {
			const isMultiple = duplicateCount > 1;
			if (infolayer) {
				infolayer.warning(
					`${isMultiple ? 'These items have' : 'This item has'} already been added to this section.`,
				);
			}
			return Promise.resolve();
		}

		if (!isTrackingDisabled) {
			newItems.forEach((item) => {
				const objectType = this.mmTeamSectionItemsService.getItemTypeName(item.objectType);
				this.analyticsService.event(TrackingEvent.TEAM_PAGE_EDITED, {
					action: ['item added'],
					objectType,
				});
			});
		}

		const teamSectionItems = newItems.map((item) => {
			const sectionItem: {
				objectType: TeamSectionItemType;
				objectId?: number;
				objectData?: RecordData.TeamSectionItem.ObjectData;
			} = {
				objectType: item.objectType,
			};

			if (item.objectId !== undefined) {
				sectionItem.objectId = item.objectId;
			}

			if (item.objectData !== undefined) {
				sectionItem.objectData = item.objectData;
			}

			return sectionItem;
		});

		return this.requestService
			.api('POST', `/teams/${teamId}/sections/${section.numericId}/section-items`, {
				teamSectionItems,
			})
			.then(
				() => {
					if (infolayer) {
						infolayer.ok(`Item${newItemsCount > 1 ? 's' : ''} added!`);
					}

					if (duplicateCount > 0) {
						this.infolayerService.warning(
							`${duplicateCount} item${duplicateCount > 1 ? 's have' : ' has'} already been added to this section.`,
						);
					}
				},
				(errorData) => {
					if (infolayer) {
						infolayer.close();
					}
					throw errorData;
				},
			)
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}

	updateOrder(teamSection: TeamSection, ids: number[]) {
		const teamId = teamSection.teamId,
			sectionId = teamSection.numericId;

		this.requestService
			.api<RecordData.Payload>('PUT', `/teams/${teamId}/sections/${sectionId}/update-order`, {
				ids,
			})
			.then((reply) => {
				this.storeService.pushPayload(reply);
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	openChangeMetricPopup(
		model: TeamSection,
		changeMetricFn: (metricSetupData: DSSetup.MetricSetupData) => void,
		metricSetupData: DSSetup.MetricSetupData | null,
		actionText?: string,
	) {
		const popup = this.popupService.createModal(TeamsSectionAddItemPopupComponent, {
			popupClass: 'popup-type-teams-section-add-item',
			allowStacked: true, // goal edit
		});
		popup.open({
			model,
			type: TeamSectionItemType.METRIC,
			metricSetupData,
			actionText,
			onSubmit: (metricSetupData: DSSetup.MetricSetupData) => {
				changeMetricFn(metricSetupData);
			},
		});
	}

	openAddPopup(data: PopupData.Teams.AddItem) {
		const popup = this.popupService.createModal(TeamsPopupAddItemComponent, {
			popupClass: 'popup-type-teams-add-item',
		});

		popup.open(data);

		return popup;
	}

	private compareFunc(item: TeamSectionItem, newItem: TeamSectionItemSnapshot) {
		if (item.objectType !== newItem.objectType) {
			return false;
		}

		if (newItem.objectType !== TeamSectionItemType.METRIC) {
			return item.objectId === newItem.objectId;
		}
		const objectData: RecordData.TeamSectionItem.ObjectData = newItem.objectData || {};

		// For metrics, check all metric-specific properties
		const newItemMetricSetup = new MetricSetup(objectData.metricSetup);
		return item.metricSetup.isMatchingMetricSetup(newItemMetricSetup);
	}

	// ------ For-store ------

	modelName = ModelName.TEAM_SECTION;

	createModel(model: TeamSectionRecord): TeamSection {
		return new TeamSection({ model });
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/team-sections': ModelManagerTeamSectionsService;
	}
}
